/* ============================================================
   AM2T — product-gallery.js
   Loaded by sections/product-gallery.liquid only.
   - Desktop: keeps the sticky first image just below the fixed
     promo bar + header (--gallery-top).
   - Mobile: thumbnail strip synced with the swipe slider.
   - Videos play only while visible (they no longer autoplay and
     download while off screen).
   ============================================================ */

(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = window.matchMedia('(max-width: 1023px)');
  const desktop = window.matchMedia('(min-width: 1024px)');

  /* --- Desktop sticky offset -----------------------------------
     Before: getBoundingClientRect() of bar + header on every scroll
     frame. Now: bar height and header height are measured on resize
     only; while scrolling we just add how much of the bar is still
     on screen, and write the CSS variable only when it changes. */
  let barHeight = 0;
  let headerHeight = 0;
  let lastTop = -1;
  let ticking = false;

  const measure = () => {
    const bar = document.querySelector('.promo-bar');
    const header = document.querySelector('.site-header');
    barHeight = bar ? bar.offsetHeight : 0;
    headerHeight = header ? header.offsetHeight : 0;
  };

  const applyTop = () => {
    ticking = false;
    if (!desktop.matches) return;
    const visibleBar = Math.max(barHeight - window.scrollY, 0);
    const top = Math.round(headerHeight + visibleBar);
    if (top === lastTop) return;
    lastTop = top;
    document.querySelectorAll('[data-product-gallery]').forEach((g) => {
      g.style.setProperty('--gallery-top', top + 'px');
    });
  };

  const requestTop = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(applyTop);
  };

  window.addEventListener('scroll', () => {
    // Only the first `barHeight` pixels of scroll change the value.
    if (window.scrollY <= barHeight + 2 || lastTop !== headerHeight) requestTop();
  }, { passive: true });
  window.addEventListener('resize', () => { measure(); lastTop = -1; requestTop(); });
  window.addEventListener('load', () => { measure(); lastTop = -1; requestTop(); });

  /* --- Per-gallery setup (re-runs in the theme editor) --------- */
  const setupGallery = (gallery) => {
    if (gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    // Videos: play when at least half visible, pause otherwise.
    const videos = gallery.querySelectorAll('video[data-autoplay]');
    if (videos.length && 'IntersectionObserver' in window && !reduceMotion) {
      const vio = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: [0, 0.5] });
      videos.forEach((v) => vio.observe(v));
    }

    const list = gallery.querySelector('.product-gallery__list');
    const strip = gallery.querySelector('.product-gallery__thumbs');
    const items = gallery.querySelectorAll('.product-gallery__item');
    const thumbs = gallery.querySelectorAll('.product-gallery__thumb');
    if (!list || !strip || !items.length || !thumbs.length) return;

    const setActive = (idx) => {
      thumbs.forEach((thumb, i) => {
        const active = i === idx;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const current = thumbs[idx];
      if (!current || !strip.clientWidth || strip.scrollWidth <= strip.clientWidth) return;
      const left = current.offsetLeft - (strip.clientWidth - current.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(left, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        const target = items[index];
        if (!target) return;
        list.scrollTo({ left: target.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        setActive(index);
      });
    });

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      if (!mobile.matches) return;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const idx = parseInt(entry.target.dataset.galleryIndex, 10);
          if (!Number.isNaN(idx)) setActive(idx);
        }
      });
    }, { root: list, threshold: [0.5, 0.6, 0.75] });
    items.forEach((item) => observer.observe(item));
  };

  window.AM2T.onSection((root) => {
    root.querySelectorAll('[data-product-gallery]').forEach(setupGallery);
    measure();
    lastTop = -1;
    applyTop();
  });
})();
